import React, { useCallback, useState, useContext, useMemo } from 'react'
import cn from 'clsx'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import Clickable from 'components/Common/Clickable'
import BodyText from 'components/Luxkit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import LinePlaneDepartureIcon from 'components/Luxkit/Icons/line/LinePlaneDepartureIcon'
import GeoContext from 'contexts/geoContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { PackageOptionViewByRoom, getActivePackagesByRoom } from 'selectors/multipleRoomBookingSelectors'
import { OfferPackageOptionView, getCheapestSelectedPackageOptionWithDefault } from 'selectors/offerPage/offerPageSelectors'
import config from 'constants/config'
import * as Analytics from 'analytics/analytics'
import { clientEvent } from 'analytics/snowplow/events'

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  ${mediaQueryUp.tablet} {
    gap: ${rem(32)};
  }

  &::after,
  &::before {
    content: '';
    flex: 1;
    border-top: 1px solid ${({ theme }) => theme.palette.neutral.default.five};
    display: block;
  }
`

const ToggleButtonsContainer = styled.div`
  display: flex;
  padding: ${rem(12)};
  gap: ${rem(12)};
  background-color: ${({ theme }) => theme.palette.neutral.default.eight};
  border: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  box-shadow: ${props => props.theme.shadow.bottom.medium};
`

const ToggleButton = styled(Clickable)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${rem(8)};
  gap: ${rem(4)};
  width: ${rem(122)};
  min-height: ${rem(90)};

  &.is-selected {
    background-color: ${({ theme }) => theme.palette.neutral.default.six};
  }

  ${mediaQueryUp.tablet} {
    width: ${rem(148)};
    min-height: ${rem(90)};
  }
`

const CenteredBodyText = styled(BodyText)`
  text-align: center;
`

interface Props {
  rooms?: Array<App.Occupants>;
  offer: App.Offer;
  multipleRoomPackages: PackageOptionViewByRoom;
  cheapestSelectedOption: OfferPackageOptionView;
}

function HotelFlightsToggleButton(props: Props) {
  const {
    rooms,
    offer,
    multipleRoomPackages,
    cheapestSelectedOption,
  } = props
  const [flightsPriceToggle, setFlightsPriceToggle] = useState<boolean>(false)
  const dispatch = useOfferPageDispatch()
  const { isTaxExclusiveRegion } = useContext(GeoContext)

  const {
    flightPrice,
    hotelPrice,
    propertyFees,
    flightsAvailable,
    hotelMemberPrice,
  } = cheapestSelectedOption

  const { multipleRoomHotelPrice, multipleRoomFlightPrice } = useMemo(() => (
    Object.values(multipleRoomPackages || {}).reduce((acc, pkg) => {
      if (!pkg) return acc

      return {
        multipleRoomHotelPrice: acc.multipleRoomHotelPrice + (isTaxExclusiveRegion ? pkg.hotelPrice : pkg.hotelPrice + (pkg.propertyFees ?? 0)),
        multipleRoomFlightPrice: acc.multipleRoomFlightPrice + pkg.flightPrice,
      }
    }, { multipleRoomHotelPrice: 0, multipleRoomFlightPrice: 0 })
  ), [isTaxExclusiveRegion, multipleRoomPackages])

  const showMemberPrice = hotelMemberPrice > 0
  const price = showMemberPrice ? hotelMemberPrice : hotelPrice
  const singleRoomHotelPrice = isTaxExclusiveRegion ? price : price + (propertyFees ?? 0)

  const showPrices = !!flightPrice && !offer.flightCacheDisabled

  const hotelTotalPrice = useMemo(() => (rooms?.length ?? 0) > 1 ? multipleRoomHotelPrice : singleRoomHotelPrice, [rooms, multipleRoomHotelPrice, singleRoomHotelPrice])
  const flightTotalPrice = useMemo(() => (rooms?.length ?? 0) > 1 ? multipleRoomFlightPrice : flightPrice, [rooms, multipleRoomFlightPrice, flightPrice])

  const onChangeToggle = useCallback((isFlightsSelected) => {
    if (flightsPriceToggle !== isFlightsSelected) {
      Analytics.track('client-event', clientEvent({
        subject: isFlightsSelected ? 'hotel_and_flights_pkg_toggle' : 'hotel_pkg_toggle',
        action: 'pressed',
        category: 'offer_page',
        type: 'interaction',
      }))
      dispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected })
      setFlightsPriceToggle(isFlightsSelected)
    }
  }, [flightsPriceToggle, dispatch])

  // Same flightsEnabled logic from FlashHotelOptionPriceDetails.tsx
  if (!flightsAvailable || !offer.offerFlightsEnabled || offer.bundledWithFlightsOnly || config.businessTraveller.currentAccountMode === 'business') return null

  return (
    <Container>
      <ToggleButtonsContainer>
        <ToggleButton className={cn({ 'is-selected': !flightsPriceToggle })} onClick={() => onChangeToggle(false)}>
          <LineBedIcon />
          <Group direction="vertical" horizontalAlign="center" gap={2}>
            <BodyText variant="large" weight="bold">Hotel</BodyText>
            {showPrices && <BodyText variant="medium">{showMemberPrice && 'From '}<FormatCurrency value={hotelTotalPrice} /></BodyText>}
          </Group>
        </ToggleButton>
        <ToggleButton className={cn({ 'is-selected': flightsPriceToggle })} onClick={() => onChangeToggle(true)}>
          <Group direction="horizontal" verticalAlign="center" gap={4}>
            <LineBedIcon />
            <LinePlaneDepartureIcon />
          </Group>
          <Group direction="vertical" horizontalAlign="center" gap={2}>
            <CenteredBodyText variant="large" weight="bold">Hotel + flights</CenteredBodyText>
            {showPrices && <BodyText variant="medium">{showMemberPrice && 'From '}<FormatCurrency value={hotelTotalPrice + flightTotalPrice} /></BodyText>}
          </Group>
        </ToggleButton>
      </ToggleButtonsContainer>
    </Container>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  offer: state.offer,
  cheapestSelectedOption: getCheapestSelectedPackageOptionWithDefault(appState, state),
  multipleRoomPackages: getActivePackagesByRoom(state, appState),
}))(HotelFlightsToggleButton)
